import React, { useRef, useState, useEffect, useCallback } from "react";
import Map, { Source, Layer, Popup, ViewState } from "react-map-gl";
import type { FeatureCollection, Point } from "geojson";
import "mapbox-gl/dist/mapbox-gl.css";
import { MdFullscreen, MdFullscreenExit } from "react-icons/md";
import CircleButton from "widgets/controls/buttons/circle-button";

const MAPBOX_TOKEN =
  "pk.eyJ1IjoiZGV3ZXRkdCIsImEiOiJja3czd3Y5ZG4wY2ZqMnJtZTA5aHhsdjhzIn0.XFDjM0cckD3GtuuMtQCRWw"; // Set your Mapbox token here

interface MarkerData {
  lat: number;
  lng: number;
  name?: string;
  snr?: string;
}

interface GeneralProps {
  markers?: MarkerData[];
}

const clusterLayer = {
  id: "clusters",
  type: "circle",
  filter: ["has", "point_count"],
  paint: {
    "circle-color": [
      "step",
      ["get", "point_count"],
      "#51bbd6",
      100,
      "#f1f075",
      750,
      "#f28cb1"
    ] as any,
    "circle-radius": [
      "step",
      ["get", "point_count"],
      20,
      100,
      30,
      750,
      40
    ] as any,
    "circle-stroke-width": 2,
    "circle-stroke-color": "#fff"
  }
};

const clusterCountLayer = {
  id: "cluster-count",
  type: "symbol",
  filter: ["has", "point_count"],
  layout: {
    "text-field": "{point_count_abbreviated}",
    "text-font": ["DIN Offc Pro Medium", "Arial Unicode MS Bold"],
    "text-size": 12
  }
};

const unclusteredPointLayer = {
  id: "unclustered-point",
  type: "circle",
  filter: ["!", ["has", "point_count"]],
  paint: {
    "circle-color": "#f28cb1",
    "circle-radius": 8,
    "circle-stroke-width": 1,
    "circle-stroke-color": "#fff"
  }
};

const initialViewState = {
  latitude: -33.805556,
  longitude: 18.869722,
  zoom: 7,
  bearing: 0,
  pitch: 0,
};

const MapCard: React.FC<GeneralProps> = ({ markers }) => {
  const [darkmode, setDarkmode] = useState(
    document.body.classList.contains("dark")
      ? "mapbox://styles/mapbox/dark-v10"
      : "mapbox://styles/mapbox/light-v10"
  );

  const [isFullscreen, setFullscreen] = useState(false);
  const [selectedMarker, setSelectedMarker] = useState<MarkerData | null>(null);
  const mapRef = useRef<any>(null);

  useEffect(() => {
    // Adjust map style based on theme changes
    const observer = new MutationObserver((mutationsList) => {
      for (const mutation of mutationsList) {
        if (
          mutation.type === "attributes" &&
          mutation.attributeName === "class"
        ) {
          if (document.body.classList.contains("dark")) {
            setDarkmode("mapbox://styles/mapbox/dark-v10");
          } else {
            setDarkmode("mapbox://styles/mapbox/light-v10");
          }
        }
      }
    });
    observer.observe(document.body, { attributes: true });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    // Disable or enable scrolling based on fullscreen mode
    if (isFullscreen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    if (mapRef.current) {
      mapRef.current.resize();
    }
  }, [isFullscreen]);

  useEffect(() => {
    // Fetch user's current location on mount
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        initialViewState.latitude = latitude;
        initialViewState.longitude = longitude;
        initialViewState.zoom = 12; // Zoom in to user's location
      },
      (error) => {
        // console.error("Error fetching location:", error);
      },
      { enableHighAccuracy: true }
    );
  }, []);

  // Convert markers to GeoJSON
  const geojson: FeatureCollection<Point, any> = {
    type: "FeatureCollection",
    features: (Array.isArray(markers) ? markers : []).map((marker) => ({
      type: "Feature",
      properties: {
        ...marker,
      },
      geometry: {
        type: "Point",
        coordinates: [marker.lng, marker.lat],
      },
    })),
  };

  // Handle cluster click to zoom in
  const onMapClick = useCallback((event: any) => {
    const mapboxSource = mapRef.current && mapRef.current.getMap().getSource("markers");
    if (!mapboxSource) return;
    const features = event.features || [];
    const cluster = features.find((f: any) => f.layer && f.layer.id === "clusters");
    if (cluster) {
      const clusterId = cluster.properties.cluster_id;
      mapboxSource.getClusterExpansionZoom(clusterId, (err: any, zoom: number) => {
        if (err) return;
        if (mapRef.current) {
          mapRef.current.flyTo({
            center: cluster.geometry.coordinates,
            zoom: zoom,
            essential: true
          });
        }
      });
    } else {
      // Unclustered point: show popup
      const unclustered = features.find((f: any) => f.layer && f.layer.id === "unclustered-point");
      if (unclustered) {
        setSelectedMarker({
          lat: unclustered.geometry.coordinates[1],
          lng: unclustered.geometry.coordinates[0],
          name: unclustered.properties.name,
          snr: unclustered.properties.snr,
        });
      } else {
        setSelectedMarker(null);
      }
    }
  }, []);

  return (
      <div
        className={`${
          isFullscreen
            ? "fixed inset-x-20 inset-y-28 rounded-2xl bg-white"
            : "h-full min-h-80"
        }`}
      >
        <Map
          ref={mapRef}
          initialViewState={initialViewState}
          style={{
            borderRadius: isFullscreen ? "16px" : "16px",
            width: "100%",
            height: "100%",
          }}
          mapStyle={darkmode}
          mapboxAccessToken={MAPBOX_TOKEN}
          interactiveLayerIds={["clusters", "unclustered-point"]}
          onClick={onMapClick}
        >
          <Source
            id="markers"
            type="geojson"
            data={geojson}
            cluster={true}
            clusterMaxZoom={14}
            clusterRadius={50}
          >
            <Layer {...(clusterLayer as any)} />
            <Layer {...(clusterCountLayer as any)} />
            <Layer {...(unclusteredPointLayer as any)} />
          </Source>

          {selectedMarker && (
            <Popup
              latitude={selectedMarker.lat}
              longitude={selectedMarker.lng}
              onClose={() => setSelectedMarker(null)}
              closeOnClick={false}
              closeButton={false}
              anchor="top"
              className="!p-0 !bg-transparent !border-none"
            >
              <div className="bg-white rounded-2xl shadow-xl p-4 w-full flex flex-col gap-2 relative">
                <h3 className="text-lg font-semibold text-gray-900 mb-1 truncate">{selectedMarker.name}</h3>
                <p className="text-sm text-gray-600 break-all">S/N: {selectedMarker.snr}</p>
              </div>
            </Popup>
          )}

          <CircleButton
            icon={isFullscreen ? MdFullscreenExit : MdFullscreen}
            onClick={() => setFullscreen(!isFullscreen)}
            extra="absolute top-4 right-4"
            size="sm"
          />
        </Map>
      </div>
  );
};

export default MapCard;
